import { QrCodeScanner } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import QrScanner from 'qr-scanner';
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSimulatorExercises } from '../api';
import reticle from '../assets/reticle.svg';
import { isGuid } from './isGuid';

const video = document.createElement('video');

const QR = () => {
  const canvasEl = useRef<HTMLCanvasElement>(null);
  const [scanResult, setScanResult] = useState<string>();
  const [scanError, setScanError] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<'unknown' | 'allow' | 'deny'>('unknown');

  const { exercises, loadingExercises, exercisesError, cancelExercises } = useSimulatorExercises({
    simulatorId: scanResult,
    onError: () => setScanError(true),
  });

  const qrScanner = new QrScanner(
    video,
    result => {
      if (result) {
        if (result.data && isGuid(result.data)) {
          setScanResult(result.data);
          qrScanner.stop();
        } else {
          setScanError(true);
          qrScanner.stop();
        }
      }
    },
    {
      onDecodeError() {
        // console.log(e);
      },
    },
  );

  useEffect(() => {
    window.navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(stream => {
        setCameraPermission('allow');
        qrScanner.start();

        // video.setAttribute('playsinline', 'true'); // required to tell iOS safari we don't want fullscreen
        video.srcObject = stream;
        video.play();

        draw();
      })
      .catch(() => {
        setCameraPermission('deny');
      });
  }, []);

  function draw() {
    const canvas = canvasEl.current;
    if (canvas && video.videoHeight && video.videoWidth) {
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    requestAnimationFrame(draw);
  }

  return (
    <>
      {cameraPermission === 'allow' ? (
        <Box display="grid" height="100%" position="absolute" sx={{ placeContent: 'center' }} top="0" width="100%">
          <Box
            component="canvas"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0 }}
            ref={canvasEl}
          ></Box>

          <Box component="img" src={reticle} alt="reticle" sx={{ position: 'relative' }} />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: 'black',
            display: 'grid',
            placeContent: 'center',
            height: '100%',
            width: '100%',
            position: 'absolute',
            top: 0,
            pl: 7,
            pr: 7,
          }}
        >
          {cameraPermission === 'deny' && (
            <>
              <Typography
                display="flex"
                alignItems="center"
                color="primary.contrastText"
                component="h1"
                variant="h5"
                gutterBottom
                gap={1}
              >
                <QrCodeScanner /> Simulator scanner
              </Typography>
              <Typography color="primary.contrastText">
                In order to use this feature, you need to provide access to your camera in your phone settings.
              </Typography>
            </>
          )}
        </Box>
      )}

      <Dialog fullWidth open={!!scanResult && !exercisesError} onClose={() => setScanResult(undefined)}>
        <DialogTitle>Simulator link detected</DialogTitle>

        {loadingExercises ? (
          <>
            <DialogContent>
              <DialogContentText>Getting link details...</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  cancelExercises();
                  setScanResult(undefined);
                  qrScanner.start();
                }}
              >
                Cancel
              </Button>
            </DialogActions>
            <LinearProgress />
          </>
        ) : exercises?.length ? (
          <>
            <DialogContent>
              <DialogContentText>
                Open link to go to the assigned exercises connected to this simulator
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setScanResult(undefined);
                  qrScanner.start();
                }}
              >
                Cancel
              </Button>
              <Button component={RouterLink} to={`./${scanResult}`} variant="contained">
                Open link
              </Button>
            </DialogActions>
          </>
        ) : (
          <DialogContent>
            <DialogContentText>There are no exercises found for this simulator</DialogContentText>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={scanError}
        onClose={() => {
          setScanError(false);
          qrScanner.start();
        }}
      >
        <DialogTitle>No simulator link detected</DialogTitle>
        <DialogContent>
          <DialogContentText>The QR you have scanned is not recognised. Please try again</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setScanResult(undefined);
              setScanError(false);
              qrScanner.start();
            }}
          >
            Try again
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QR;
